import { plainFix } from '@bartizo/shared';
import { useState, type ReactNode } from 'react';
import { Button } from '../ui/Button.js';

/** A command to copy: shown as written, with a button that puts it on the clipboard. */
function CodeBlock({ code }: { code: string }): ReactNode {
  const [copied, setCopied] = useState(false);
  return (
    <div className="code-copy">
      <pre className="mono text-sm code-block">{code}</pre>
      <Button
        onClick={() => {
          navigator.clipboard
            .writeText(code)
            .then(() => {
              setCopied(true);
              window.setTimeout(() => {
                setCopied(false);
              }, 2000);
            })
            .catch(() => undefined);
        }}
      >
        {copied ? 'Copied' : 'Copy'}
      </Button>
    </div>
  );
}

/**
 * "How to fix it", as steps: what to do, with the commands set apart so they can be
 * copied. The benchmark's own paragraph stays one click away, since it is what the
 * check was written from. Nothing here runs anything.
 */
export function FixSteps({
  title,
  remediation,
}: {
  title: string;
  remediation: string;
}): ReactNode {
  const steps = plainFix(title, remediation);
  if (steps.length === 0) return <p className="text-sm">{remediation}</p>;
  return (
    <>
      <ol className="fix-steps text-sm">
        {steps.map((step, index) => (
          <li key={`${String(index)}|${step.text}`}>
            <span>{step.text}</span>
            {step.code !== undefined && <CodeBlock code={step.code} />}
          </li>
        ))}
      </ol>
      <details className="fix-original">
        <summary className="text-sm">The benchmark’s own wording</summary>
        <p className="text-sm">{remediation}</p>
      </details>
    </>
  );
}
